import type { BlockyTimeDB } from './schema'
import { db as defaultDb } from './schema'
import type { CategoryRow, EventTypeRow, ObjectGroupRow } from './types'
import { SEED_CATEGORY_IDS, SEED_GROUP_IDS, SEED_TYPE_IDS } from './seedIds'
import { getDeviceId, getSyncState, setSyncState } from '@/core/repository/syncStateRepository'
import { ensureSettings } from '@/core/repository/settingsRepository'
import { logger } from '@/core/logging/logger'

/**
 * First-run data.
 *
 * A fresh install must never show an empty picker -- that is a dead end for a
 * new user, and this is a tap-first app where the first tap has to work.
 */

interface SeedType {
  id: string
  name: string
  shortcutText: string
  colorHex: string
}

interface SeedCategory {
  id: string
  name: string
  colorHex: string
  types: SeedType[]
}

/** Palette from 02-data-model.md section 11. */
export const SEED_CATEGORIES: SeedCategory[] = [
  {
    id: SEED_CATEGORY_IDS.rest,
    name: 'Rest',
    colorHex: '#7E8CA0',
    types: [
      { id: SEED_TYPE_IDS.sleep, name: 'Sleep', shortcutText: 'Sleep', colorHex: '#6B7A90' },
      { id: SEED_TYPE_IDS.nap, name: 'Nap', shortcutText: 'Nap', colorHex: '#8A97AA' },
      { id: SEED_TYPE_IDS.relax, name: 'Relax', shortcutText: 'Relax', colorHex: '#9AA6B7' },
    ],
  },
  {
    id: SEED_CATEGORY_IDS.work,
    name: 'Work',
    colorHex: '#4A90D9',
    types: [
      { id: SEED_TYPE_IDS.focus, name: 'Focus', shortcutText: 'Focus', colorHex: '#2E77C4' },
      { id: SEED_TYPE_IDS.meeting, name: 'Meeting', shortcutText: 'Meet', colorHex: '#4A90D9' },
      { id: SEED_TYPE_IDS.email, name: 'Email', shortcutText: 'Email', colorHex: '#69A6E4' },
      { id: SEED_TYPE_IDS.admin, name: 'Admin', shortcutText: 'Admin', colorHex: '#8ABBEE' },
    ],
  },
  {
    id: SEED_CATEGORY_IDS.health,
    name: 'Health',
    colorHex: '#4CAF7D',
    types: [
      { id: SEED_TYPE_IDS.exercise, name: 'Exercise', shortcutText: 'Gym', colorHex: '#2F9963' },
      { id: SEED_TYPE_IDS.walk, name: 'Walk', shortcutText: 'Walk', colorHex: '#4CAF7D' },
      { id: SEED_TYPE_IDS.cook, name: 'Cook', shortcutText: 'Cook', colorHex: '#6BC295' },
      { id: SEED_TYPE_IDS.eat, name: 'Eat', shortcutText: 'Eat', colorHex: '#8AD3AE' },
    ],
  },
  {
    id: SEED_CATEGORY_IDS.learn,
    name: 'Learn',
    colorHex: '#C9803E',
    types: [
      { id: SEED_TYPE_IDS.read, name: 'Read', shortcutText: 'Read', colorHex: '#B06B2C' },
      { id: SEED_TYPE_IDS.study, name: 'Study', shortcutText: 'Study', colorHex: '#C9803E' },
      { id: SEED_TYPE_IDS.practice, name: 'Practice', shortcutText: 'Prac', colorHex: '#DB9A5F' },
    ],
  },
  {
    id: SEED_CATEGORY_IDS.life,
    name: 'Life',
    colorHex: '#B05FA8',
    types: [
      { id: SEED_TYPE_IDS.commute, name: 'Commute', shortcutText: 'Travel', colorHex: '#96468E' },
      { id: SEED_TYPE_IDS.chores, name: 'Chores', shortcutText: 'Chores', colorHex: '#B05FA8' },
      { id: SEED_TYPE_IDS.family, name: 'Family', shortcutText: 'Family', colorHex: '#C57ABD' },
      { id: SEED_TYPE_IDS.social, name: 'Social', shortcutText: 'Social', colorHex: '#D79AD1' },
    ],
  },
  {
    id: SEED_CATEGORY_IDS.leisure,
    name: 'Leisure',
    colorHex: '#D9534F',
    types: [
      { id: SEED_TYPE_IDS.games, name: 'Games', shortcutText: 'Games', colorHex: '#C03B37' },
      { id: SEED_TYPE_IDS.video, name: 'Video', shortcutText: 'Video', colorHex: '#D9534F' },
      { id: SEED_TYPE_IDS.music, name: 'Music', shortcutText: 'Music', colorHex: '#E4736F' },
      { id: SEED_TYPE_IDS.browse, name: 'Browse', shortcutText: 'Browse', colorHex: '#EE9390' },
    ],
  },
]

export interface SeedOptions {
  /** Skip the "has this device ever synced" guard. Tests only. */
  force?: boolean
}

/**
 * Seeds the catalogue if, and only if, this looks like a genuinely fresh
 * install. Three independent guards, because seeding twice is unrecoverable:
 *
 *   1. Fixed ids, so even a double seed collapses to one row (seedIds.ts).
 *   2. A `seeded` marker in syncState.
 *   3. A check that no categories already exist -- which covers the case where
 *      a pull arrived before seeding ran.
 */
export async function seedIfEmpty(
  db: BlockyTimeDB = defaultDb,
  options: SeedOptions = {},
): Promise<boolean> {
  await ensureSettings(db)

  if (!options.force) {
    if ((await getSyncState('seeded', db)) === '1') return false
    if ((await db.categories.count()) > 0) {
      // Data arrived from sync before we got here. Mark as seeded so we never
      // add a duplicate catalogue on top of it.
      await setSyncState('seeded', '1', db)
      return false
    }
  }

  const deviceId = await getDeviceId(db)
  const now = Date.now()

  const categories: CategoryRow[] = []
  const eventTypes: EventTypeRow[] = []

  SEED_CATEGORIES.forEach((cat, catIndex) => {
    categories.push({
      id: cat.id,
      name: cat.name,
      colorHex: cat.colorHex,
      icon: null,
      sortOrder: catIndex,
      isCollapsed: 0,
      isArchived: 0,
      updatedAt: now,
      deletedAt: null,
      deviceId,
      serverSeq: 0,
      _dirty: 1,
    })

    cat.types.forEach((type, typeIndex) => {
      eventTypes.push({
        id: type.id,
        categoryId: cat.id,
        name: type.name,
        shortcutText: type.shortcutText,
        colorHex: type.colorHex,
        icon: null,
        defaultObjectId: null,
        sortOrder: typeIndex,
        isPinned: 0,
        isArchived: 0,
        updatedAt: now,
        deletedAt: null,
        deviceId,
        serverSeq: 0,
        _dirty: 1,
      })
    })
  })

  // One empty group so the second dimension is discoverable.
  const objectGroups: ObjectGroupRow[] = [
    {
      id: SEED_GROUP_IDS.projects,
      name: 'Projects',
      sortOrder: 0,
      isArchived: 0,
      updatedAt: now,
      deletedAt: null,
      deviceId,
      serverSeq: 0,
      _dirty: 1,
    },
  ]

  await db.transaction('rw', db.categories, db.eventTypes, db.objectGroups, db.syncState, async () => {
    await db.categories.bulkPut(categories)
    await db.eventTypes.bulkPut(eventTypes)
    await db.objectGroups.bulkPut(objectGroups)
    await setSyncState('seeded', '1', db)
  })

  logger.info('db', `Seeded ${categories.length} categories, ${eventTypes.length} event types`)
  return true
}
